import { Capacitor } from '@capacitor/core';

/**
 * Device Service
 *
 * Centralno mesto za identitet instalacije na native platformama:
 * - generiše stabilan `deviceId` samo jednom (prvi start),
 * - čuva ga persistentno preko `@capacitor/preferences`,
 * - vraća `ios` / `android` platformu (nikada `web`),
 * - daje deviceName za prikaz na ekranu aktivnih uređaja.
 *
 * Web aplikacija NE generiše i NE čita deviceId - sve metode vraćaju `null`
 * kada `Capacitor.isNativePlatform()` nije true.
 */

export const DEVICE_ID_STORAGE_KEY = 'cuvarkuca_device_id';

const SUPPORTED_PLATFORMS = ['ios', 'android'];

// In-memory keš: isti deviceId se koristi do kraja života procesa.
let cachedDeviceId = null;
let deviceIdPromise = null;
let cachedDeviceName = null;

const isNativePlatform = () => Capacitor.isNativePlatform();

/**
 * Napravi UUID v4 bez dodatnih dependency-ja.
 * @returns {string} UUID
 */
const generateDeviceId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const getLocalStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (error) {
    return null;
  }
};

const readFromPreferences = async () => {
  const { Preferences } = await import('@capacitor/preferences');
  const { value } = await Preferences.get({ key: DEVICE_ID_STORAGE_KEY });
  return value || null;
};

const persistToPreferences = async (deviceId) => {
  const { Preferences } = await import('@capacitor/preferences');
  await Preferences.set({ key: DEVICE_ID_STORAGE_KEY, value: deviceId });
};

/**
 * Pročitaj postojeći deviceId. Preferences je primarni mehanizam; ako plugin
 * nije dostupan (npr. native projekat još nije sync-ovan), koristi se
 * localStorage WebView-a kao sigurnosna mreža.
 * @returns {Promise<string|null>}
 */
const readStoredDeviceId = async () => {
  try {
    const stored = await readFromPreferences();
    if (stored) return stored;
  } catch (error) {
    console.warn(
      'DeviceId: Preferences nije dostupan, pokušavam localStorage.',
      error?.message || error
    );
  }

  const storage = getLocalStorage();
  return storage ? storage.getItem(DEVICE_ID_STORAGE_KEY) : null;
};

const persistDeviceId = async (deviceId) => {
  try {
    await persistToPreferences(deviceId);
    return;
  } catch (error) {
    console.warn(
      'DeviceId: upis u Preferences nije uspeo, koristim localStorage.',
      error?.message || error
    );
  }

  const storage = getLocalStorage();
  if (storage) {
    storage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  }
};

const readOrCreateDeviceId = async () => {
  const stored = await readStoredDeviceId();
  if (stored) return stored;

  const generated = generateDeviceId();
  await persistDeviceId(generated);
  return generated;
};

const deviceService = {
  /**
   * Da li se aplikacija izvršava kao native (iOS/Android) build.
   * @returns {boolean}
   */
  isNative() {
    return isNativePlatform();
  },

  /**
   * Native platforma za backend (`ios` ili `android`). Za web vraća `null`,
   * tako da se `web` nikada ne šalje backend-u.
   * @returns {'ios'|'android'|null}
   */
  getPlatform() {
    if (!isNativePlatform()) return null;
    const platform = Capacitor.getPlatform();
    return SUPPORTED_PLATFORMS.includes(platform) ? platform : null;
  },

  /**
   * Vrati stabilan deviceId instalacije. Prvi poziv ga generiše i čuva,
   * svaki sledeći vraća istu vrednost (i u narednim pokretanjima aplikacije).
   * Na web-u vraća `null` i ne dira nijedan native API.
   * @returns {Promise<string|null>}
   */
  async getDeviceId() {
    if (!isNativePlatform()) return null;
    if (cachedDeviceId) return cachedDeviceId;

    if (!deviceIdPromise) {
      deviceIdPromise = readOrCreateDeviceId()
        .then((deviceId) => {
          cachedDeviceId = deviceId;
          return deviceId;
        })
        .finally(() => {
          deviceIdPromise = null;
        });
    }

    return deviceIdPromise;
  },

  /**
   * deviceId koji se šalje u LoginDto. Greška pri čitanju lokalnog skladišta
   * ne sme da blokira prijavu - tada se prijava nastavlja bez deviceId-a.
   * @returns {Promise<string|null>}
   */
  async getDeviceIdForLogin() {
    if (!isNativePlatform()) return null;
    try {
      return await this.getDeviceId();
    } catch (error) {
      console.warn(
        'DeviceId: čitanje nije uspelo, prijava se nastavlja bez deviceId-a.',
        error?.message || error
      );
      return null;
    }
  },

  /**
   * Naziv/model uređaja za prikaz (nije security identifikator).
   * Ako ga nije moguće pouzdano dobiti, koristi se jednostavan fallback.
   * @returns {Promise<string|null>}
   */
  async getDeviceName() {
    if (!isNativePlatform()) return null;
    if (cachedDeviceName) return cachedDeviceName;

    const platform = this.getPlatform();
    const fallback =
      platform === 'ios' ? 'iOS device' : platform === 'android' ? 'Android device' : null;

    try {
      const { Device } = await import('@capacitor/device');
      const info = await Device.getInfo();
      cachedDeviceName = info?.name || info?.model || fallback;
    } catch (error) {
      console.warn('DeviceName nije dostupan, koristi se fallback.', error?.message || error);
      cachedDeviceName = fallback;
    }

    return cachedDeviceName;
  },

  /**
   * Native bootstrap: obezbedi da deviceId postoji već na prvom startu.
   * Na web-u je no-op.
   * @returns {Promise<string|null>}
   */
  async initialize() {
    if (!isNativePlatform()) return null;
    try {
      return await this.getDeviceId();
    } catch (error) {
      console.warn('DeviceId: inicijalizacija nije uspela.', error?.message || error);
      return null;
    }
  },
};

export default deviceService;

